/**
 * @fileoverview Sudoku Validator Utilities
 * @description Helper functions for validating Sudoku puzzles
 */

import { CellValue, SudokuCell, SudokuGrid } from "../types";

/**
 * Converts a puzzle string (81 chars) to a grid array
 * @param puzzleString - 81 character string where 0 = empty
 * @param solutionString - 81 character solution string
 * @returns Array of 81 SudokuCell objects
 */
export const puzzleStringToGrid = (
  puzzleString: string,
  solutionString: string
): SudokuGrid => {
  const grid: SudokuGrid = [];

  for (let i = 0; i < 81; i++) {
    const char = puzzleString[i];
    const value = char === "0" ? null : (parseInt(char, 10) as CellValue);
    const isGiven = value !== null;

    grid.push({
      value,
      status: isGiven ? "given" : "user",
      isGiven,
      notes: [],
      row: Math.floor(i / 9),
      col: i % 9,
    });
  }

  return grid;
};

/**
 * Converts a grid back to a puzzle string
 * @param grid - Array of 81 SudokuCell objects
 * @returns 81 character string where 0 = empty
 */
export const gridToPuzzleString = (grid: SudokuGrid): string => {
  return grid.map((cell) => (cell.value === null ? "0" : cell.value)).join("");
};

/**
 * Gets the row, column, and box indices for a cell
 * @param index - Cell index (0-80)
 */
export const getCellPosition = (index: number) => {
  const row = Math.floor(index / 9);
  const col = index % 9;
  const box = Math.floor(row / 3) * 3 + Math.floor(col / 3);
  return { row, col, box };
};

/**
 * Gets all cell indices in the same row
 */
export const getRowIndices = (index: number): number[] => {
  const { row } = getCellPosition(index);
  const indices: number[] = [];
  for (let col = 0; col < 9; col++) {
    indices.push(row * 9 + col);
  }
  return indices;
};

/**
 * Gets all cell indices in the same column
 */
export const getColIndices = (index: number): number[] => {
  const { col } = getCellPosition(index);
  const indices: number[] = [];
  for (let row = 0; row < 9; row++) {
    indices.push(row * 9 + col);
  }
  return indices;
};

/**
 * Gets all cell indices in the same 3x3 box
 */
export const getBoxIndices = (index: number): number[] => {
  const { row, col } = getCellPosition(index);
  const boxStartRow = Math.floor(row / 3) * 3;
  const boxStartCol = Math.floor(col / 3) * 3;

  const indices: number[] = [];
  for (let r = boxStartRow; r < boxStartRow + 3; r++) {
    for (let c = boxStartCol; c < boxStartCol + 3; c++) {
      indices.push(r * 9 + c);
    }
  }
  return indices;
};

/**
 * Gets all related cell indices (same row, column, or box)
 */
export const getRelatedIndices = (index: number): number[] => {
  const rowIndices = getRowIndices(index);
  const colIndices = getColIndices(index);
  const boxIndices = getBoxIndices(index);

  const uniqueIndices = new Set([...rowIndices, ...colIndices, ...boxIndices]);
  uniqueIndices.delete(index); // Remove the cell itself

  return Array.from(uniqueIndices);
};

/**
 * Checks if a value is valid for a cell (no conflicts in row, col, box)
 * @param puzzleString - Current puzzle state as 81-char string
 * @param index - Cell index to check
 * @param value - Value to validate
 * @returns true if the value doesn't conflict with existing values
 */
export const isValidPlacement = (
  puzzleString: string,
  index: number,
  value: number
): boolean => {
  const relatedIndices = getRelatedIndices(index);

  for (const relIdx of relatedIndices) {
    const existingValue = puzzleString[relIdx];
    if (existingValue !== "0" && parseInt(existingValue, 10) === value) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if a cell value matches the solution
 */
export const isCorrectValue = (
  solution: string,
  index: number,
  value: number
): boolean => {
  const correctValue = parseInt(solution[index], 10);
  return value === correctValue;
};

/**
 * Checks if the puzzle is completed (all cells filled correctly)
 */
export const isPuzzleComplete = (
  currentState: string,
  solution: string
): boolean => {
  return currentState === solution;
};

/**
 * Counts how many cells still need to be filled
 */
export const getRemainingCells = (puzzleString: string): number => {
  return puzzleString.split("").filter((c) => c === "0").length;
};

/**
 * Gets cells with a specific value
 */
export const getCellsWithValue = (
  puzzleString: string,
  value: number
): number[] => {
  const indices: number[] = [];
  for (let i = 0; i < 81; i++) {
    if (puzzleString[i] === value.toString()) {
      indices.push(i);
    }
  }
  return indices;
};

/**
 * Counts how many of each number (1-9) are placed
 */
export const getNumberCounts = (
  puzzleString: string
): Record<number, number> => {
  const counts: Record<number, number> = {};
  for (let n = 1; n <= 9; n++) {
    counts[n] = 0;
  }

  for (const char of puzzleString) {
    const num = parseInt(char, 10);
    if (num >= 1 && num <= 9) {
      counts[num]++;
    }
  }

  return counts;
};

/**
 * Gets which numbers are "complete" (appear 9 times)
 */
export const getCompletedNumbers = (puzzleString: string): number[] => {
  const counts = getNumberCounts(puzzleString);
  return Object.entries(counts)
    .filter(([_, count]) => count === 9)
    .map(([num]) => parseInt(num, 10));
};

/**
 * Finds all conflicting cells for a given cell
 */
export const findConflicts = (
  puzzleString: string,
  index: number
): number[] => {
  const value = puzzleString[index];
  if (value === "0") return [];

  const relatedIndices = getRelatedIndices(index);
  const conflicts: number[] = [];

  for (const relIdx of relatedIndices) {
    if (puzzleString[relIdx] === value) {
      conflicts.push(relIdx);
    }
  }

  return conflicts;
};

/**
 * Gets a hint (reveals the correct value for an empty cell)
 * @param current - Current puzzle state
 * @param solution - Solution
 * @param preferSelected - Optional: prefer the selected cell if it's empty
 * @returns The index of the cell to reveal, or -1 if puzzle is complete
 */
export const getHintCell = (
  current: string,
  solution: string,
  preferSelected?: number | null
): number => {
  // If a cell is selected and it's empty or wrong, use that
  if (preferSelected !== null && preferSelected !== undefined) {
    const currentValue = current[preferSelected];
    const correctValue = solution[preferSelected];
    if (currentValue !== correctValue) {
      return preferSelected;
    }
  }

  // Otherwise find first empty or wrong cell
  for (let i = 0; i < 81; i++) {
    if (current[i] !== solution[i]) {
      return i;
    }
  }

  return -1; // Puzzle is complete
};

/**
 * Formats time in seconds to MM:SS display
 */
export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Formats time in seconds to descriptive text
 */
export const formatTimeVerbose = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds} sec`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (secs === 0) {
    return `${mins} min`;
  }
  return `${mins} min ${secs} sec`;
};

